import axios from 'axios';
import {
  URL_WALLET_EMAILS,
  URL_WALLET_EMAIL_VALIDATE,
  URL_WALLET_EMAIL_UPDATE,
  URL_WALLET_VERIFY_OTP,
  URL_WALLET_RESEND_OTP,
  URL_WALLET_EMAIL_VERIFICATION,
  URL_WALLET_VERIFICATION_STATUS,
  URL_WALLET_EMAIL_SET_DEFAULT,
} from '../constants/urls';
import { fetchUser } from './user';
import validEmail from '../utils/validEmail';

export const FETCH_WALLET_EMAILS_BEGIN = 'FETCH_WALLET_EMAILS_BEGIN';
export const FETCH_WALLET_EMAILS_SUCCESS = 'FETCH_WALLET_EMAILS_SUCCESS';
export const FETCH_WALLET_EMAILS_FAILURE = 'FETCH_WALLET_EMAILS_FAILURE';

export const UPDATE_USE_FOR_BEGIN = 'UPDATE_USE_FOR_BEGIN';
export const UPDATE_USE_FOR_SUCCESS = 'UPDATE_USE_FOR_SUCCESS';
export const UPDATE_USE_FOR_FAILURE = 'UPDATE_USE_FOR_FAILURE';

export const SET_DEFAULT_EMAIL_BEGIN = 'SET_DEFAULT_EMAIL_BEGIN';
export const SET_DEFAULT_EMAIL_SUCCESS = 'SET_DEFAULT_EMAIL_SUCCESS';
export const SET_DEFAULT_EMAIL_FAILURE = 'SET_DEFAULT_EMAIL_FAILURE';

export const REMOVE_EMAIL_BEGIN = 'REMOVE_EMAIL_BEGIN';
export const REMOVE_EMAIL_SUCCESS = 'REMOVE_EMAIL_SUCCESS';
export const REMOVE_EMAIL_FAILURE = 'REMOVE_EMAIL_FAILURE';

export const VALIDATE_EMAIL_BEGIN = 'VALIDATE_EMAIL_BEGIN';
export const VALIDATE_EMAIL_SUCCESS = 'VALIDATE_EMAIL_SUCCESS';
export const VALIDATE_EMAIL_FAILURE = 'VALIDATE_EMAIL_FAILURE';

export const ADD_EMAIL_BEGIN = 'ADD_EMAIL_BEGIN';
export const ADD_EMAIL_SUCCESS = 'ADD_EMAIL_SUCCESS';
export const ADD_EMAIL_FAILURE = 'ADD_EMAIL_FAILURE';

export const VERIFY_OTP_BEGIN = 'VERIFY_OTP_BEGIN';
export const VERIFY_OTP_SUCCESS = 'VERIFY_OTP_SUCCESS';
export const VERIFY_OTP_FAILURE = 'VERIFY_OTP_FAILURE';

export const DISMISS_VERIFIED_NOTIFICATION = 'DISMISS_VERIFIED_NOTIFICATION';
// AXP-1613: cross-device magic-link completion (pollVerificationStatus) has
// no email object to drive VERIFY_OTP_SUCCESS's own justVerifiedEmailId
// logic — this sets it directly once the post-poll refetch locates the row.
export const MARK_JUST_VERIFIED = 'MARK_JUST_VERIFIED';

export const RESEND_OTP_BEGIN = 'RESEND_OTP_BEGIN';
export const RESEND_OTP_SUCCESS = 'RESEND_OTP_SUCCESS';
export const RESEND_OTP_FAILURE = 'RESEND_OTP_FAILURE';

export const CANCEL_OTP = 'CANCEL_OTP';
export const RESEND_VERIFICATION = 'RESEND_VERIFICATION';

export const RESEND_VERIFICATION_BEGIN = 'RESEND_VERIFICATION_BEGIN';
export const RESEND_VERIFICATION_SUCCESS = 'RESEND_VERIFICATION_SUCCESS';
export const RESEND_VERIFICATION_FAILURE = 'RESEND_VERIFICATION_FAILURE';

export const CLEAR_REMOVE_ERROR = 'CLEAR_REMOVE_ERROR';
export const CLEAR_CLASSIFICATION = 'CLEAR_CLASSIFICATION';
export const CLEAR_ADD_ERROR = 'CLEAR_ADD_ERROR';
export const CLEAR_DEFAULT_EMAIL_ERROR = 'CLEAR_DEFAULT_EMAIL_ERROR';
export const SET_INLINE_MODE = 'SET_INLINE_MODE';
export const CLEAR_INLINE = 'CLEAR_INLINE';

export const fetchWalletEmails = () => async (dispatch) => {
  dispatch({ type: FETCH_WALLET_EMAILS_BEGIN });
  try {
    const response = await axios.get(URL_WALLET_EMAILS);
    dispatch({ type: FETCH_WALLET_EMAILS_SUCCESS, payload: response.data });
  } catch (error) {
    dispatch({
      type: FETCH_WALLET_EMAILS_FAILURE,
      payload: {
        errorCode: error?.response?.data?.errorCode,
        error: error?.response?.data?.error || error.message,
      },
    });
  }
};

export const updateUseFor = (emailId, useFor) => async (dispatch) => {
  dispatch({ type: UPDATE_USE_FOR_BEGIN, payload: { emailId, useFor } });
  try {
    await axios.put(URL_WALLET_EMAIL_UPDATE.join(emailId), {
      use_for: useFor,
    });
    dispatch({ type: UPDATE_USE_FOR_SUCCESS, payload: { emailId, useFor } });
  } catch (error) {
    dispatch({
      type: UPDATE_USE_FOR_FAILURE,
      payload: {
        emailId,
        error: error?.response?.data?.error || error.message,
      },
    });
  }
};

export const removeEmail =
  (emailId, { newDefaultId } = {}) =>
  async (dispatch) => {
    dispatch({ type: REMOVE_EMAIL_BEGIN, payload: { emailId } });
    try {
      await axios.post(`${URL_WALLET_EMAILS}/${emailId}/remove`, {
        new_default_email_id: newDefaultId || undefined,
      });
      dispatch({ type: REMOVE_EMAIL_SUCCESS, payload: { emailId } });
      if (newDefaultId) {
        dispatch(fetchWalletEmails());
      }
    } catch (error) {
      dispatch({
        type: REMOVE_EMAIL_FAILURE,
        payload: {
          emailId,
          errorCode: error?.response?.data?.errorCode,
          error: error?.response?.data?.error || error.message,
        },
      });
    }
  };

export const validateEmail = (address) => async (dispatch) => {
  dispatch({ type: VALIDATE_EMAIL_BEGIN });

  // AXP-1641: catch obviously-malformed input immediately, client-side,
  // rather than round-tripping to the server just to get the same
  // INVALID_EMAIL shape back. Uses the same validEmail check the legacy
  // (non-wallet) add-email flow already validates against, so this isn't
  // a new/diverging rule.
  if (!validEmail(address)) {
    dispatch({
      type: VALIDATE_EMAIL_FAILURE,
      payload: {
        source: 'validate',
        errorCode: 'INVALID_EMAIL',
      },
    });
    return;
  }

  try {
    const response = await axios.post(URL_WALLET_EMAIL_VALIDATE, {
      email: address,
    });
    // The validate endpoint always returns HTTP 200; format / MX / banned-char
    // rejections surface as { valid: false }. Synthesize an errorCode so the
    // UI can render an inline error and gate submit until the user retries.
    // Any classification returned alongside `valid: false` is intentionally
    // discarded — no domain badge should display for an invalid address.
    if (response.data?.valid === false) {
      dispatch({
        type: VALIDATE_EMAIL_FAILURE,
        payload: {
          source: 'validate',
          errorCode: 'INVALID_EMAIL',
        },
      });
      return;
    }
    dispatch({ type: VALIDATE_EMAIL_SUCCESS, payload: response.data });
  } catch (error) {
    const data = error?.response?.data;
    dispatch({
      type: VALIDATE_EMAIL_FAILURE,
      payload: {
        source: 'validate',
        errorCode: data?.errorCode,
        error: data?.error || error.message,
        attemptsRemaining: data?.attemptsRemaining,
      },
    });
  }
};

// AXP-1613: this initiates OTP verification. The entity itself may already
// exist server-side at this point — account-api's Wallet::Emails#start!
// calls EmailsAPI.create before initiating OTP, for the non-collision case —
// but a collision creates nothing for this wallet, and the response is
// identical either way: it never carries email_id/classification, so the
// client genuinely can't tell which happened until OTP succeeds and
// finalize! runs (that's deliberate — see EmailCollisionAlert). Treat
// pendingEmail as a placeholder for that reason, not because the entity is
// provably absent.
export const addEmail = (address, useFor) => async (dispatch) => {
  dispatch({ type: ADD_EMAIL_BEGIN });
  try {
    const response = await axios.post(URL_WALLET_EMAILS, {
      address,
      use_for: useFor,
    });
    dispatch({ type: ADD_EMAIL_SUCCESS, payload: response.data });
  } catch (error) {
    const data = error?.response?.data;
    dispatch({
      type: ADD_EMAIL_FAILURE,
      payload: {
        source: 'add',
        errorCode: data?.errorCode,
        error: data?.error || error.message,
        attemptsRemaining: data?.attemptsRemaining,
      },
    });

    // ALREADY_PENDING means this wallet already has an unconfirmed entity
    // for this address from an earlier attempt. Whether it's already in
    // local state depends on whether a fetch happened since it was
    // created — refetch unconditionally rather than assume either way, so
    // the row the error message points the user toward is actually there.
    if (data?.errorCode === 'ALREADY_PENDING') {
      dispatch(fetchWalletEmails());
    }

    // Note: when attemptsRemaining === 0, the server has already destroyed
    // the session (warden.logout). We intentionally do NOT dispatch
    // FETCH_SESSION_FAILURE here — that would trigger AuthRoute to redirect
    // before the WalletLockedModal can render. The modal's close/contact
    // handlers will navigate away, and any subsequent API call will get 401.
  }
};

export const verifyOtp =
  (verificationId, code, intent = null) =>
  async (dispatch, getState) => {
    dispatch({ type: VERIFY_OTP_BEGIN });
    try {
      const response = await axios.post(
        URL_WALLET_VERIFY_OTP.join(verificationId),
        { code },
      );
      // Capture targetEmailId BEFORE VERIFY_OTP_SUCCESS resets inline.
      const targetEmailId = getState().walletEmails.inline.targetEmailId;
      dispatch({
        type: VERIFY_OTP_SUCCESS,
        payload: {
          verificationId,
          verified_at: response.data?.verified_at,
          // AXP-1613: the add-email flow's created entity, serialized by
          // account-api — present only for that flow, not resend-verification
          // (which updates an email already in the list by targetEmailId).
          email: response.data?.email,
        },
      });
      if (intent === 'set_default' && targetEmailId) {
        dispatch(setDefaultEmail(targetEmailId, { afterVerification: true }));
      }
    } catch (error) {
      const data = error?.response?.data;
      dispatch({
        type: VERIFY_OTP_FAILURE,
        payload: {
          source: 'verify',
          errorCode: data?.errorCode,
          error: data?.error || error.message,
          remainingAttempts: data?.remainingAttempts,
          // Present only on a cross-wallet collision
          widgetManifest: data?.manifest,
          bffBaseUrl: data?.baseUrls?.workflowBffBaseUrl,
          collisionEmail: data?.email,
        },
      });
    }
  };

export const resendOtp = (verificationId) => async (dispatch) => {
  dispatch({ type: RESEND_OTP_BEGIN });
  try {
    await axios.post(URL_WALLET_RESEND_OTP.join(verificationId));
    dispatch({ type: RESEND_OTP_SUCCESS });
  } catch (error) {
    const data = error?.response?.data;
    dispatch({
      type: RESEND_OTP_FAILURE,
      payload: {
        source: 'resend',
        errorCode: data?.errorCode,
        error: data?.error || error.message,
      },
    });
  }
};

export const setInlineMode = (mode) => ({
  type: SET_INLINE_MODE,
  payload: mode,
});

export const clearInline = () => ({ type: CLEAR_INLINE });

// Cancel OTP. A real entity may already exist server-side for this address
// (EmailsAPI.create runs at Submit time, before OTP, for the non-collision
// case) — but a collision creates nothing, and the client can't tell which
// happened. Rather than guess, refetch: if an entity exists it'll show up as
// a real, authoritative row; if not, nothing appears. Only refetch when there
// was actually a pendingEmail — cancelling a resend-verification session, or
// one that never got as far as ADD_EMAIL_SUCCESS, has nothing new to pick up.
export const cancelOtp = () => (dispatch, getState) => {
  const hadPendingEmail = !!getState().walletEmails.inline.pendingEmail;
  dispatch({ type: CANCEL_OTP });
  if (hadPendingEmail) {
    dispatch(fetchWalletEmails());
  }
};

export const dismissVerifiedNotification = () => ({
  type: DISMISS_VERIFIED_NOTIFICATION,
});

export const resendVerification =
  (emailId, classification, intent = null) =>
  async (dispatch) => {
    dispatch({ type: RESEND_VERIFICATION_BEGIN });
    try {
      const response = await axios.post(
        URL_WALLET_EMAIL_VERIFICATION.join(emailId),
      );
      dispatch({
        type: RESEND_VERIFICATION_SUCCESS,
        payload: {
          emailId,
          classification,
          verificationId: response.data?.verification_id,
          intent,
        },
      });
    } catch (error) {
      const data = error?.response?.data;
      dispatch({
        type: RESEND_VERIFICATION_FAILURE,
        payload: {
          emailId,
          classification,
          errorCode: data?.errorCode,
          error: data?.error || error.message,
        },
      });
    }
  };

// Poll verification status for cross-device OTP completion (AXP-1251).
// When the user completes the magic link on another device, the status
// endpoint returns status=completed, outcome=passed. This thunk dispatches
// VERIFY_OTP_SUCCESS to complete the flow without manual OTP entry.
// Non-success states are silently ignored — polling will continue or be
// cancelled by the component. Terminal errors (401/404/410 — the
// verification session is no longer valid) surface as an expired-code error
// so the panel doesn't appear to hang indefinitely (AXP-1518); other errors
// (network, 5xx) are treated as transient and polling continues.
// signal: optional AbortSignal for cancelling in-flight requests. When aborted,
// returns early without logging.
const POLL_TERMINAL_STATUSES = [401, 404, 410];

export const pollVerificationStatus =
  (verificationId, signal) => async (dispatch, getState) => {
    try {
      const response = await axios.get(
        URL_WALLET_VERIFICATION_STATUS.join(verificationId),
        { signal },
      );

      // AXP-1613: outcome=collision means this OTP succeeded against a
      // cross-wallet collision, completed by the actual owner clicking the
      // link on their own device — not the requester's own success. Route
      // through VERIFY_OTP_FAILURE/ALREADY_EXISTS, same as the code-entry
      // collision reveal, rather than treating "completed" as this
      // requester's own success.
      if (
        response.data.status === 'completed' &&
        response.data.outcome === 'collision'
      ) {
        dispatch({
          type: VERIFY_OTP_FAILURE,
          payload: {
            source: 'poll',
            errorCode: 'ALREADY_EXISTS',
            error: 'Email is already in use',
          },
        });
        return;
      }

      if (
        response.data.status === 'completed' &&
        response.data.outcome === 'passed'
      ) {
        // Capture intent/targetEmailId/pendingEmail address BEFORE
        // VERIFY_OTP_SUCCESS resets inline.
        const { intent, targetEmailId, pendingEmail } =
          getState().walletEmails.inline;
        dispatch({
          type: VERIFY_OTP_SUCCESS,
          payload: {
            verificationId,
            verified_at: new Date().toISOString(),
            // AXP-1613: unlike verifyOtp, /status doesn't return the created
            // email (it only reads cached OTP status, it doesn't call
            // finalize!) — refetch below to pick up the authoritative row.
          },
        });
        if (intent === 'set_default' && targetEmailId) {
          dispatch(setDefaultEmail(targetEmailId, { afterVerification: true }));
        } else if (!targetEmailId) {
          // Add-flow, completed via cross-device magic link: no local
          // pendingEmail has the real entity data, so refetch to replace the
          // placeholder row with the server's authoritative state.
          await dispatch(fetchWalletEmails());
          // VERIFY_OTP_SUCCESS's add-flow branch only sets
          // justVerifiedEmailId from a payload.email it never gets here (see
          // comment above) — find the row the refetch just brought in and
          // mark it directly so the "Email Verified" notification still
          // shows for this cross-device path.
          const verifiedEmail = pendingEmail?.address
            ? getState().walletEmails.emails.find(
                (email) => email.address === pendingEmail.address,
              )
            : null;
          if (verifiedEmail) {
            dispatch({
              type: MARK_JUST_VERIFIED,
              payload: { emailId: verifiedEmail.email_id },
            });
          }
        }
        return;
      }
      if (response.data.status === 'expired') {
        dispatch({
          type: VERIFY_OTP_FAILURE,
          payload: { source: 'poll', errorCode: 'CODE_EXPIRED' },
        });
        return;
      }
      // For all other states (initiated, pending, failed, unknown), silently
      // continue — polling will repeat on the next interval.
    } catch (error) {
      // Return early if the request was cancelled via AbortSignal
      if (error.name === 'CanceledError') {
        return;
      }
      if (POLL_TERMINAL_STATUSES.includes(error?.response?.status)) {
        dispatch({
          type: VERIFY_OTP_FAILURE,
          payload: { source: 'poll', errorCode: 'CODE_EXPIRED' },
        });
        return;
      }
      // Transient failures (network timeouts, 5xx, etc.) — log for debugging
      // and keep polling; a single failed request shouldn't disrupt the
      // user's OTP entry experience.
      console.warn('[pollVerificationStatus]', error?.message || error);
    }
  };

// afterVerification: true when this dispatch was chained from a successful
// OTP verification (the user clicked "Set as default" on an unverified
// email). The reducer stores it on `defaultEmailError` so the banner can
// surface an OTP-success-but-default-failed message variant.
export const setDefaultEmail =
  (emailId, { afterVerification = false } = {}) =>
  async (dispatch) => {
    dispatch({ type: SET_DEFAULT_EMAIL_BEGIN, payload: { emailId } });
    try {
      const response = await axios.post(
        URL_WALLET_EMAIL_SET_DEFAULT.join(emailId),
        {},
      );
      dispatch({
        type: SET_DEFAULT_EMAIL_SUCCESS,
        payload: response.data,
      });
      // Refresh user state to update the email field
      dispatch(fetchUser());
    } catch (error) {
      const data = error?.response?.data;
      // The endpoint renders {error, errorCode} for handled failures, or
      // {message} with no errorCode if the global exception handler catches.
      // The {errors: [...]} shape is Emails::ChangePrimary's internal envelope,
      // which Wallet::Emails#set_default translates into its own error classes
      // server-side — it never reaches the wire here, so that fallback is
      // vestigial. Kept because a test pins it and it costs nothing.
      const errorMessage = data?.error || data?.errors?.[0] || error.message;
      dispatch({
        type: SET_DEFAULT_EMAIL_FAILURE,
        payload: {
          emailId,
          error: errorMessage,
          errorCode: data?.errorCode,
          afterVerification,
        },
      });
    }
  };

export const clearDefaultEmailError = () => ({
  type: CLEAR_DEFAULT_EMAIL_ERROR,
});

export const clearAddError = () => ({ type: CLEAR_ADD_ERROR });
